// src/types/constants.ts

// --- Therapist Statuses ---
export const THERAPIST_STATUS = {
  NEW_CLIENT: 'New Client',
  TRIAL: 'Trial',
  ACTIVE: 'Active',
  DEACTIVATED: 'Deactivated',
} as const; // 'as const' makes keys readonly and values literal types

// Type derived from the constant object's values
export type TherapistStatus =
  (typeof THERAPIST_STATUS)[keyof typeof THERAPIST_STATUS];

// --- Package Options ---
// Define package types - easy to add more later
export const PACKAGE_TYPE = {
  BASIC: 'Basic',
  // PRO: 'Pro',
  // ENTERPRISE: 'Enterprise',
} as const;
export type PackageType = (typeof PACKAGE_TYPE)[keyof typeof PACKAGE_TYPE];

// --- Patient Statuses ---
export const PATIENT_STATUS = {
  PROSPECT: 'Prospect', // Initial inquiry/lead stage
  ONBOARDING: 'Onboarding', // Committed, pending contract/forms/first session
  ACTIVE: 'Active', // Currently receiving therapy
  CLOSED: 'Closed',
} as const;
export type PatientStatus =
  (typeof PATIENT_STATUS)[keyof typeof PATIENT_STATUS];

// --- Session Statuses ---
export const SESSION_STATUS = {
  SCHEDULED: 'Scheduled',
  COMPLETED: 'Completed',
  CANCELLED: 'Cancelled',
  NO_SHOW: 'No Show',
  INVOICED: 'Invoiced', // <<< ADDED for Billingo Integration
} as const;
export type SessionStatus =
  (typeof SESSION_STATUS)[keyof typeof SESSION_STATUS];

// --- Session Types ---
// NOTE: This is likely just a type alias now, ensure firestoreTypes.ts uses 'string'
// If you want runtime constants, keep this, otherwise remove if only using strings.
export const SESSION_TYPE = {
  IN_PERSON: 'In-Person',
  VIDEO: 'Video Call', // Renamed from VIDEO
  PHONE: 'Phone Call', // Renamed from PHONE
  UNKNOWN: 'Unknown', // Added for default case
} as const;
// Type derived from the constant object's values
export type SessionType = (typeof SESSION_TYPE)[keyof typeof SESSION_TYPE];

// --- Session Creation Methods ---
export const CREATION_METHOD = {
  MANUAL: 'Manual',
  RECURRING: 'Recurring',
  AUTOMATIC: 'Automatic', // Used by Calendar Sync
} as const;
export type CreationMethod =
  (typeof CREATION_METHOD)[keyof typeof CREATION_METHOD];

// --- Change Log Actors ---
export const CHANGE_LOG_ACTOR = {
  CALENDAR_SYNC: 'Calendar Sync',
  SYSTEM: 'System',
  // Add USER: `User: ${userId}` pattern when implemented
} as const;
export type ChangeLogActor =
  | (typeof CHANGE_LOG_ACTOR)[keyof typeof CHANGE_LOG_ACTOR]
  | `User: ${string}`; // Allow specific user format

// --- Change Log Types ---
export const CHANGE_LOG_TYPE = {
  CREATED: 'CREATED', // Example for initial creation if logged
  CANCELLED_VIA_CALENDAR: 'CANCELLED_VIA_CALENDAR',
  RESCHEDULED_VIA_CALENDAR: 'RESCHEDULED_VIA_CALENDAR',
  STATUS_UPDATED_MANUALLY: 'STATUS_UPDATED_MANUALLY', // Example for future UI changes
  DETAILS_UPDATED_MANUALLY: 'DETAILS_UPDATED_MANUALLY', // Example
  INVOICE_GENERATED: 'INVOICE_GENERATED', // <<< ADDED for Billingo Integration
  // Add specific invoice send statuses if needed, e.g.:
  // INVOICE_SENT: "INVOICE_SENT",
  // INVOICE_SEND_FAILED: "INVOICE_SEND_FAILED",
  // INVOICE_SEND_SKIPPED: "INVOICE_SEND_SKIPPED",
} as const;
export type ChangeLogType =
  (typeof CHANGE_LOG_TYPE)[keyof typeof CHANGE_LOG_TYPE];

// --- UI Helper Options ---

// Helper array for generating dropdown options easily (example for Therapist Status)
export const THERAPIST_STATUS_OPTIONS = Object.values(THERAPIST_STATUS).map(
  (status) => ({
    value: status,
    label: status,
  })
);

// Add similar option arrays for other constants as needed for UI components
export const PATIENT_STATUS_OPTIONS = Object.values(PATIENT_STATUS).map(
  (status) => ({
    value: status,
    label: status,
  })
);

// Updated Session Status Options including 'Invoiced'
export const SESSION_STATUS_OPTIONS = Object.values(SESSION_STATUS).map(
  (status) => ({
    value: status,
    label: status,
  })
);

// Assuming SESSION_TYPE is still used for UI options, even if DB uses string
export const SESSION_TYPE_OPTIONS = Object.values(SESSION_TYPE).map((type) => ({
  value: type,
  label: type,
}));

// Add the missing PACKAGE_TYPE_OPTIONS export
export const PACKAGE_TYPE_OPTIONS = Object.values(PACKAGE_TYPE).map((pkg) => ({
  value: pkg,
  label: pkg,
}));

// Note: Billingo-related UI options (like BILLINGO_PAYMENT_METHOD_OPTIONS)
// should be generated from the constants in billingoConstants.ts
// where those constants are defined.
